import axiosInstance from "./axiosConfig";

/**
 * Service for handling order-related operations
 */
export const orderService = {
  /**
   * Get user's orders with optional pagination
   * @param {number} page - Page number (1-indexed for API)
   * @param {number} limit - Number of items per page
   * @returns {Promise<Object>} - Promise resolving to paginated order data { orders: [], pagination: {} }
   */
  async getUserOrders(page = 1, limit = 10) {
    try {
      const response = await axiosInstance.get(
        `/orders/myorders?page=${page}&limit=${limit}`
      );
      return response.data;
    } catch (error) {
      console.error(
        "Service Error: Fetching user orders failed:",
        error.response?.data || error.message
      );
      throw error;
    }
  },

  /**
   * Get admin orders with optional pagination and filters
   * @param {number} page - Page number (1-indexed for API)
   * @param {number} limit - Number of items per page
   * @param {string} status - Optional filter by order status
   * @param {string} userId - Deprecated - kept for backward compatibility
   * @param {string} search - Optional search term for order number, email or company name
   * @returns {Promise<Object>} - Promise resolving to paginated order data { orders: [], pagination: {} }
   */
  async getAdminOrders(
    page = 1,
    limit = 10,
    status = "",
    userId = "", // Kept for backward compatibility
    search = ""
  ) {
    try {
      let url = `/admin/orders?page=${page}&limit=${limit}`;
      if (status) url += `&status=${status}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;

      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error(
        "Service Error: Fetching admin orders failed:",
        error.response?.data || error.message
      );
      throw error;
    }
  },

  /**
   * Get order details by ID (Handles both admin and user contexts)
   * @param {string} orderId - The order ID to fetch
   * @param {boolean} isAdmin - Flag indicating if the request is from an admin context
   * @returns {Promise<Object>} - Promise resolving to order data
   */
  async getOrderById(orderId, isAdmin = false) {
    try {
      const endpoint = isAdmin
        ? `/admin/orders/${orderId}`
        : `/orders/${orderId}`;
      const response = await axiosInstance.get(endpoint);

      // Add utility method for tracking if tracking info is available
      if (
        response.data.metadata?.trackingNumber &&
        response.data.shippingMethod?.trackingUrlFormat
      ) {
        response.data.trackingUrl =
          response.data.shippingMethod.trackingUrlFormat.replace(
            "{trackingNumber}",
            response.data.metadata.trackingNumber
          );
      } else if (response.data.metadata?.trackingNumber) {
        response.data.trackingUrl = `https://track.yourpackage.com/${response.data.metadata.trackingNumber}`;
      }

      return response.data;
    } catch (error) {
      console.error(
        "Order service error:",
        error.response?.data || error.message
      );
      throw error;
    }
  },

  // --- Admin Actions ---

  /**
   * Confirm manual bank transfer payment for an order (admin only)
   * @param {string} orderId - The order ID to confirm payment for
   * @param {string} [transactionId] - Optional transaction reference from admin
   * @returns {Promise<Object>} - Promise resolving to updated order data
   */
  async confirmManualPayment(orderId, transactionId = undefined) {
    try {
      const payload = transactionId ? { transactionId } : {};
      const response = await axiosInstance.post(
        `/admin/orders/${orderId}/confirm-payment`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error(
        `Service Error: Confirming manual payment for order ${orderId} failed:`,
        error.response?.data || error.message
      );
      throw error;
    }
  },

  /**
   * Mark an order as shipped (admin only)
   * @param {string} orderId - The order ID to mark as shipped
   * @param {string} [trackingNumber] - Optional tracking number
   * @returns {Promise<Object>} - Promise resolving to updated order data
   */
  async markAsShipped(orderId, trackingNumber = undefined) {
    try {
      console.log(`Preparing to send shipping request for order ${orderId}`);
      console.log(`Tracking number type: ${typeof trackingNumber}`);
      console.log(`Tracking number value: "${trackingNumber}"`);

      // Ensure tracking number is passed as an empty string if undefined, null or falsy
      const payload = { trackingNumber: trackingNumber || "" };

      console.log("Payload being sent:", payload);

      const response = await axiosInstance.post(
        `/admin/orders/${orderId}/ship`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error(
        `Service Error: Marking order ${orderId} as shipped failed:`,
        error.response?.data || error.message
      );
      throw error;
    }
  },

  /**
   * Cancel an order (admin only)
   * @param {string} orderId - The order ID to cancel
   * @returns {Promise<Object>} - Promise resolving to updated order data
   */
  async cancelOrderAdmin(orderId) {
    try {
      const response = await axiosInstance.post(
        `/admin/orders/${orderId}/cancel`
      );
      return response.data;
    } catch (error) {
      console.error(
        `Service Error: Canceling order ${orderId} failed:`,
        error.response?.data || error.message
      );
      throw error;
    }
  },

  /**
   * Update internal notes for an order (admin only)
   * @param {string} orderId - The order ID to update notes for
   * @param {string} notes - The notes content
   * @returns {Promise<Object>} - Promise resolving to updated order data
   */
  async updateOrderNotes(orderId, notes) {
    try {
      const response = await axiosInstance.put(
        `/admin/orders/${orderId}/notes`,
        {
          notes,
        }
      );
      return response.data;
    } catch (error) {
      console.error(
        `Service Error: Updating notes for order ${orderId} failed:`,
        error.response?.data || error.message
      );
      throw error;
    }
  },

  // --- User Actions ---

  /**
   * Confirm delivery of an order (user only)
   * @param {string} orderId - The order ID to confirm delivery for
   * @returns {Promise<Object>} - Promise resolving to updated order data
   */
  async confirmDelivery(orderId) {
    try {
      const response = await axiosInstance.post(
        `/orders/${orderId}/confirm-delivery`
      );
      return response.data;
    } catch (error) {
      console.error(
        `Service Error: Confirming delivery for order ${orderId} failed:`,
        error.response?.data || error.message
      );
      throw error;
    }
  },

  /**
   * Request a return for an order (user only)
   * @param {string} orderId - The order ID to request return for
   * @param {string} reason - Overall reason for the return
   * @param {Array} items - Array of items to return with details { orderItemId, quantity, reason }
   * @returns {Promise<Object>} - Promise resolving to updated order data
   */
  async requestReturn(orderId, reason, items = []) {
    try {
      const payload = {
        reason,
        items,
      };

      const response = await axiosInstance.post(
        `/orders/${orderId}/request-return`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error(
        `Service Error: Requesting return for order ${orderId} failed:`,
        error.response?.data || error.message
      );
      throw error;
    }
  },

  /**
   * Cancel an order (user only, specific conditions apply)
   * @param {string} orderId - The order ID to cancel
   * @returns {Promise<Object>} - Promise resolving to updated order data
   */
  async cancelOrderUser(orderId) {
    try {
      const response = await axiosInstance.post(`/orders/${orderId}/cancel`);
      return response.data;
    } catch (error) {
      console.error(
        `Service Error: User canceling order ${orderId} failed:`,
        error.response?.data || error.message
      );
      throw error;
    }
  },

  /**
   * Download invoice PDF for an order
   * @param {string} orderId - The order ID to download invoice for
   * @param {boolean} isAdmin - Flag indicating if the request is from an admin context
   * @param {string} variant - Optional variant for admin ('paid' or 'awaiting')
   * @returns {Promise<Blob>} - Promise resolving to PDF blob
   */
  async downloadInvoice(orderId, isAdmin = false, variant = null) {
    try {
      const endpoint = isAdmin
        ? `/admin/orders/${orderId}/invoice${variant ? `?variant=${variant}` : ""}`
        : `/orders/${orderId}/invoice`;

      const response = await axiosInstance.get(endpoint, {
        responseType: "blob",
      });

      return response.data;
    } catch (error) {
      console.error(
        `Service Error: Downloading invoice for order ${orderId} failed:`,
        error.response?.data || error.message
      );
      throw error;
    }
  },

  // Deprecated - Use specific action functions instead
  // async updateOrderStatus(orderId, updateData) { ... },
};
